import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Star, Phone, Mail, MapPin, Award, Brain, Heart, Users, Clock, Shield } from "lucide-react";
import { DOCTOR, SPECIALTIES, ARTICLES, TESTIMONIOS } from "../data/content";
import SEO from "../components/SEO";

export default function Home() {
  return (
    <div>
      <SEO
        canonical="/"
        description="Dr. César S. Martínez Maldonado, psiquiatra en Xalapa, Veracruz. Atención integral en psiquiatría y neurología: depresión, ansiedad, esquizofrenia, TDAH, adicciones y más. 40 años de experiencia."
      />
      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-50 via-cyan-50/40 to-teal-50/30">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-cyan-200/30 via-transparent to-transparent" />
        <div className="relative max-w-7xl mx-auto px-4 pt-14 pb-20 lg:pt-20 lg:pb-28 grid lg:grid-cols-2 gap-10 items-center">
          <div className="order-2 lg:order-1">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/80 backdrop-blur border border-cyan-100 text-cyan-800 text-sm font-medium mb-6 shadow-sm">
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              {DOCTOR.yearsExperience} años de experiencia clínica
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.05]">
              Salud mental <span className="bg-gradient-to-r from-cyan-600 to-teal-600 bg-clip-text text-transparent">con dignidad</span> y confianza
            </h1>
            <p className="mt-6 text-lg md:text-xl text-slate-600 leading-relaxed max-w-xl">
              Atención médica integral en psiquiatría y neurología a cargo del {DOCTOR.name}. Tratamiento respetuoso, confidencial y basado en evidencia.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/contacto"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-gradient-to-r from-cyan-600 to-teal-600 text-white font-semibold shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all"
              >
                Agendar consulta <ArrowRight size={18} />
              </Link>
              <a
                href={`tel:${DOCTOR.phone.replace(/\s/g, "")}`}
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white border-2 border-slate-200 hover:border-cyan-600 text-slate-800 font-semibold transition-all"
              >
                <Phone size={18} /> {DOCTOR.phone}
              </a>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-6 max-w-md">
              <Stat value="40+" label="Años de experiencia" />
              <Stat value="9" label="Áreas clínicas" />
              <Stat value="100%" label="Confidencialidad" />
            </div>
          </div>

          <div className="order-1 lg:order-2 relative">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <img
                src="/images/consultorio.jpg"
                alt="Salud mental - amanecer y esperanza"
                className="w-full h-[400px] md:h-[500px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/30 via-transparent to-transparent" />
            </div>
            <div className="hidden md:flex absolute -bottom-6 -left-6 bg-white p-5 rounded-2xl shadow-xl gap-3 items-center border border-slate-100 max-w-xs">
              <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                <Heart className="text-emerald-600" size={22} />
              </div>
              <div>
                <div className="font-bold text-slate-900">Atención cálida</div>
                <div className="text-xs text-slate-500">Trato digno y humano</div>
              </div>
            </div>
            <div className="hidden md:flex absolute -top-4 -right-4 bg-white p-4 rounded-2xl shadow-xl gap-3 items-center border border-slate-100">
              <Award className="text-cyan-600" size={24} />
              <div className="text-sm font-semibold text-slate-800">APM Certificado</div>
            </div>
          </div>
        </div>
      </section>

      {/* DOCTOR INFO */}
      <section className="max-w-7xl mx-auto px-4 py-16 lg:py-24">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative">
            <img
              src="/images/doctor.jpg"
              alt="Consultorio médico profesional"
              className="w-full h-[460px] object-cover rounded-3xl shadow-xl"
            />
            <div className="absolute -bottom-6 -right-6 bg-gradient-to-br from-cyan-600 to-teal-600 text-white p-6 rounded-2xl shadow-xl max-w-[220px]">
              <div className="text-4xl font-bold">40+</div>
              <div className="text-sm opacity-95 mt-1">años de práctica clínica en psiquiatría</div>
            </div>
          </div>
          <div>
            <div className="text-cyan-700 font-semibold text-sm uppercase tracking-wider mb-3">Sobre el doctor</div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 leading-tight mb-5">
              {DOCTOR.name}
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              Profesionista, médico especializado en Psiquiatría, comprometido y maduro con experiencia de varios años en el tratamiento de las enfermedades mentales, a nivel institucional y privado.
            </p>
            <p className="text-slate-600 leading-relaxed mb-6">
              Conozco la complejidad de las enfermedades mentales y propongo un tratamiento y trato digno, en las mejores condiciones de confidencialidad, con el mayor respeto, involucrando a la familia para una rehabilitación completa.
            </p>
            <ul className="space-y-3 mb-8">
              {[
                "Médico Cirujano por la UV, especializado en Psiquiatría General",
                "Co-fundador del Hospital de Salud Mental Dr. Miguel Dorantes Mesa (1979)",
                "Afiliado a la Asociación Psiquiátrica Mexicana (APM) A.C.",
                "Diplomado en Tratamiento Integral del Alcoholismo y Fármaco Dependencias",
                "Diplomado en Tanatología, Gestalt y Psicoterapias Cognoscitivistas",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3 text-slate-700">
                  <CheckCircle2 size={20} className="text-emerald-600 flex-shrink-0 mt-0.5" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
            <Link
              to="/curriculum"
              className="inline-flex items-center gap-2 text-cyan-700 font-semibold hover:text-cyan-900 transition"
            >
              Ver currículum completo <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* SPECIALTIES */}
      <section className="bg-slate-50 py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <div className="text-cyan-700 font-semibold text-sm uppercase tracking-wider mb-3">Especialidades clínicas</div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 leading-tight">
              9 áreas de atención especializada
            </h2>
            <p className="mt-4 text-slate-600 text-lg">
              Atención integral para niños, adolescentes, adultos y adultos mayores en diversas condiciones neurológicas y psiquiátricas.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {SPECIALTIES.map((s) => (
              <Link
                key={s.slug}
                to={`/especialidades/${s.slug}`}
                className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl border border-slate-100 transition-all duration-300 hover:-translate-y-1"
              >
                <div className="relative h-48 overflow-hidden">
                  <img src={s.image} alt={s.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-slate-900/20 to-transparent" />
                  <div className="absolute top-4 left-4 w-11 h-11 rounded-xl bg-white/95 backdrop-blur flex items-center justify-center text-2xl shadow-md">
                    {s.emoji}
                  </div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="text-white text-xl font-bold">{s.title}</h3>
                  </div>
                </div>
                <div className="p-5">
                  <p className="text-slate-600 text-sm line-clamp-2 mb-4">{s.short}</p>
                  <div className="flex items-center gap-2 text-cyan-700 font-semibold text-sm group-hover:gap-3 transition-all">
                    Conocer más <ArrowRight size={16} />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="max-w-7xl mx-auto px-4 py-16 lg:py-24">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="text-cyan-700 font-semibold text-sm uppercase tracking-wider mb-3">Mi compromiso contigo</div>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900">
            Principios que guían mi práctica
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: Shield, title: "Confidencialidad absoluta", desc: "Tu privacidad es sagrada. Toda información clínica se maneja con la máxima discreción y bajo secreto profesional." },
            { icon: Brain, title: "Basado en evidencia", desc: "Diagnósticos y tratamientos sustentados en la mejor evidencia científica disponible, adaptados a cada paciente." },
            { icon: Heart, title: "Trato humano y cálido", desc: "Empatía, respeto y acompañamiento en cada paso del proceso terapéutico, involucrando a la familia." },
            { icon: Users, title: "Atención familiar integral", desc: "La familia es parte clave del tratamiento. Ofrecemos psicoeducación y contención para seres queridos." },
            { icon: Clock, title: "Soporte continuo", desc: "Soporte telefónico permanente mientras dure el tratamiento. No estás solo en el proceso." },
            { icon: Award, title: "Experiencia comprobada", desc: "Más de 29 años en psiquiatría hospitalaria e institucional con miles de pacientes atendidos." },
          ].map(({ icon: Icon, title, desc }) => (
            <div key={title} className="bg-white rounded-2xl p-7 border border-slate-100 shadow-sm hover:shadow-lg transition-all hover:-translate-y-1">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-100 to-teal-100 flex items-center justify-center mb-4">
                <Icon className="text-cyan-700" size={22} />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">{title}</h3>
              <p className="text-slate-600 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ARTICLES */}
      <section className="bg-gradient-to-br from-slate-50 to-cyan-50/30 py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div className="max-w-2xl">
              <div className="text-cyan-700 font-semibold text-sm uppercase tracking-wider mb-3">Recursos educativos</div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900">
                Artículos y material educativo
              </h2>
              <p className="mt-4 text-slate-600 text-lg">
                Información especializada y actualizada sobre salud mental para pacientes y familiares.
              </p>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {ARTICLES.slice(0, 6).map((a) => (
              <Link
                key={a.slug}
                to={`/articulos/${a.slug}`}
                className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className="relative h-52 overflow-hidden">
                  <img src={a.image} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <span className="absolute top-3 left-3 px-3 py-1 rounded-full bg-white/95 backdrop-blur text-xs font-semibold text-cyan-700">
                    {a.category}
                  </span>
                </div>
                <div className="p-5">
                  <h3 className="font-bold text-slate-900 text-lg leading-tight mb-2 line-clamp-2 group-hover:text-cyan-700 transition">
                    {a.title}
                  </h3>
                  <p className="text-slate-600 text-sm line-clamp-2">{a.excerpt}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="max-w-7xl mx-auto px-4 py-16 lg:py-24">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="text-cyan-700 font-semibold text-sm uppercase tracking-wider mb-3">Testimonios</div>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900">
            Historias de recuperación y esperanza
          </h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {TESTIMONIOS.slice(0, 3).map((t, i) => (
            <div key={i} className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm hover:shadow-lg transition-all">
              <div className="flex gap-0.5 mb-3">
                {Array.from({ length: t.rating }).map((_, idx) => (
                  <Star key={idx} size={16} className="fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <p className="text-slate-700 leading-relaxed mb-5">"{t.text}"</p>
              <div className="flex items-center gap-3 pt-4 border-t border-slate-100">
                <div className="w-11 h-11 rounded-full bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center text-white font-bold">
                  {t.name.charAt(0)}
                </div>
                <div>
                  <div className="font-semibold text-slate-900">{t.name}</div>
                  <div className="text-xs text-slate-500">{t.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="text-center mt-10">
          <Link to="/testimonios" className="inline-flex items-center gap-2 text-cyan-700 font-semibold hover:text-cyan-900">
            Ver todos los testimonios <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 pb-16">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-cyan-600 via-teal-600 to-emerald-700 px-8 py-14 md:py-20 text-center text-white shadow-2xl">
          <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, white 0%, transparent 40%), radial-gradient(circle at 80% 80%, white 0%, transparent 40%)" }} />
          <div className="relative">
            <h2 className="text-3xl md:text-5xl font-bold leading-tight max-w-3xl mx-auto">
              Da el primer paso hacia tu bienestar mental
            </h2>
            <p className="mt-5 text-lg md:text-xl text-cyan-50 max-w-2xl mx-auto">
              Atención personalizada, discreta y profesional. Agenda tu cita hoy mismo.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link
                to="/contacto"
                className="px-7 py-3.5 rounded-full bg-white text-cyan-700 font-semibold shadow-lg hover:scale-105 transition-all inline-flex items-center gap-2"
              >
                <Mail size={18} /> Enviar mensaje
              </Link>
              <a
                href={`tel:${DOCTOR.phone.replace(/\s/g, "")}`}
                className="px-7 py-3.5 rounded-full bg-white/10 backdrop-blur border-2 border-white/30 text-white font-semibold hover:bg-white/20 transition-all inline-flex items-center gap-2"
              >
                <Phone size={18} /> {DOCTOR.phone}
              </a>
            </div>
            <div className="mt-8 flex flex-wrap justify-center items-center gap-6 text-sm text-cyan-50">
              <span className="flex items-center gap-1.5"><MapPin size={14} /> Xalapa, Veracruz</span>
              <span className="flex items-center gap-1.5"><MapPin size={14} /> Martínez de la Torre</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Stat({ value, label }) {
  return (
    <div>
      <div className="text-2xl md:text-3xl font-bold text-slate-900">{value}</div>
      <div className="text-xs md:text-sm text-slate-500 mt-1">{label}</div>
    </div>
  );
}
